// References Manager Component
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Save, X } from 'lucide-react';
import { Button } from '../ui/button';
import { ImageUploadField } from './AdminShared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReferencesManager = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { fetchReferences(); }, []);

  const fetchReferences = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/references/all`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setReferences(data || []);
      }
    } catch (err) {
      console.error('Error fetching references:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette référence ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/references/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchReferences();
    } catch (err) {
      console.error('Error deleting reference:', err);
    }
  };

  const handleSave = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/references/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setEditingId(null);
      fetchReferences();
    } catch (err) {
      console.error('Error saving reference:', err);
    }
  };

  const handleAdd = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/references`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setShowAddForm(false);
      setEditForm({});
      fetchReferences();
    } catch (err) {
      console.error('Error adding reference:', err);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-slate-800">Références</h2>
        <Button onClick={() => {
          setShowAddForm(true);
          setEditForm({ image: '', title_en: '', title_fr: '', title_ar: '', order: references.length, is_active: true });
        }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {showAddForm && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-800 mb-4">Nouvelle Référence</h3>
          <div className="space-y-4">
            <ImageUploadField value={editForm.image} onChange={(url) => setEditForm({...editForm, image: url})} label="Image" />
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Titre EN</label>
                <input type="text" value={editForm.title_en || ''} onChange={(e) => setEditForm({...editForm, title_en: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Titre FR</label>
                <input type="text" value={editForm.title_fr || ''} onChange={(e) => setEditForm({...editForm, title_fr: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 العنوان AR</label>
                <input type="text" value={editForm.title_ar || ''} onChange={(e) => setEditForm({...editForm, title_ar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}><X className="w-4 h-4 mr-1" /> Annuler</Button>
              <Button size="sm" onClick={handleAdd}><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {references.map((ref) => (
          <div key={ref.id} className="border border-slate-200 rounded-lg overflow-hidden">
            <img src={ref.image} alt="" className="w-full h-32 object-cover" />
            <div className="p-3">
              <div className="flex justify-between items-start">
                <div className="text-sm text-slate-600 truncate">{ref.title_fr || 'Sans titre'}</div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditingId(ref.id); setEditForm(ref); }} className="p-1 hover:bg-slate-100 rounded"><Edit className="w-4 h-4 text-slate-400" /></button>
                  <button onClick={() => handleDelete(ref.id)} className="p-1 hover:bg-slate-100 rounded"><Trash2 className="w-4 h-4 text-red-400" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {references.length === 0 && !showAddForm && <div className="text-center py-12 text-slate-500">Aucune référence.</div>}
    </div>
  );
};

export default ReferencesManager;
